import { api } from "@/lib/api";
import { getLang, FR_VALUES } from "@/lib/i18n";

const ATTRS = ["placeholder", "aria-label", "title", "alt"];
const SKIP = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "TEXTAREA", "INPUT"]);
const NATIVE = /[éèêëàâçùûôîïœ«»]/i;
let dict = {};
const pending = new Set();
const asked = new Set();
const orig = new WeakMap();
const mine = new WeakMap();
const origAttr = new WeakMap();
let timer = null;

const ENGLISH = /\b(the|and|of|to|your|you|is|are|for|with|this|after|before|continue|start|try|review|lesson|lessons|level|back|open|next)\b/i;
const needs = (s) => {
  const t = s.trim();
  return t.length > 1 && t.length <= 2000 && /[a-z]{2}/i.test(t) && (!NATIVE.test(t) || ENGLISH.test(t)) && !FR_VALUES.has(t)
    && !/^[\w.+-]+@[\w.-]+\.\w+$/.test(t) && !/^(https?:|\/)/.test(t) && !/^[a-z0-9]+(_[a-z0-9]+)+$/.test(t)
    && !/^[\d\s/.:,\-–·%~()+]*(AM|PM|min|h|s)?[\d\s/.:,\-–·%~()+]*$/i.test(t);
};

const lookup = (s) => {
  const t = s.trim();
  if (!needs(t)) return s;
  if (dict[t] != null) return s.replace(t, dict[t]);
  if (!asked.has(t)) { pending.add(t); schedule(); }
  return s;
};

const blocked = (el) => !el || SKIP.has(el.tagName) || blockedAttr(el);
const blockedAttr = (el) => !el || el.closest("[contenteditable='true']") || el.closest("[translate]")?.getAttribute("translate") === "no";

const setText = (node, v) => {
  mine.set(node, v);
  if (node.nodeValue !== v) node.nodeValue = v;
};

const runsOf = (parent) => {
  const runs = [];
  let cur = [];
  parent.childNodes.forEach((n) => {
    if (n.nodeType === 3) cur.push(n);
    else if (cur.length) { runs.push(cur); cur = []; }
  });
  if (cur.length) runs.push(cur);
  return runs;
};

const applyRun = (run) => {
  run.forEach((n) => { if (!orig.has(n) || (mine.has(n) && n.nodeValue !== mine.get(n))) orig.set(n, n.nodeValue); });
  const src = run.map((n) => orig.get(n)).join("");
  const out = lookup(src);
  if (out === src) { run.forEach((n) => setText(n, orig.get(n))); return; }
  run.forEach((n, i) => setText(n, i === 0 ? out : ""));
};

const applyParent = (el) => {
  if (blocked(el)) return;
  runsOf(el).forEach(applyRun);
};

const applyAttrs = (el) => {
  if (blockedAttr(el)) return;
  ATTRS.forEach((a) => {
    const v = el.getAttribute(a);
    if (v == null) return;
    const store = origAttr.get(el) || {};
    if (store[a] == null || (v !== store[`${a}_out`])) store[a] = v;
    const out = lookup(store[a]);
    store[`${a}_out`] = out;
    origAttr.set(el, store);
    if (out !== v) el.setAttribute(a, out);
  });
};

const scan = (root) => {
  if (root.nodeType === 3) { if (root.parentElement) applyParent(root.parentElement); return; }
  if (root.nodeType !== 1) return;
  const parents = new Set();
  const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let n = w.nextNode(); n; n = w.nextNode()) if (n.parentElement) parents.add(n.parentElement);
  parents.forEach(applyParent);
  [root, ...root.querySelectorAll(ATTRS.map((a) => `[${a}]`).join(","))].forEach((el) => el.getAttribute && applyAttrs(el));
};

function schedule() {
  if (timer) return;
  timer = setTimeout(flush, 250);
}

async function flush() {
  timer = null;
  const batch = [...pending].slice(0, 60);
  batch.forEach((t) => { pending.delete(t); asked.add(t); });
  if (!batch.length) return;
  try {
    const { data } = await api.post(`/i18n/${getLang()}/translate`, { texts: batch });
    Object.assign(dict, data);
    if (Object.keys(data).length) scan(document.body);
  } catch {
    // keep English when translation is unavailable
  }
  if (pending.size) schedule();
}

const onMutations = (muts) => {
  muts.forEach((m) => {
    if (m.type === "characterData") {
      const n = m.target;
      if (n.nodeValue === mine.get(n)) return;
      orig.set(n, n.nodeValue);
      if (n.parentElement) applyParent(n.parentElement);
    } else if (m.type === "attributes") {
      const s = origAttr.get(m.target);
      if (s && m.target.getAttribute(m.attributeName) === s[`${m.attributeName}_out`]) return;
      applyAttrs(m.target);
    } else {
      m.addedNodes.forEach(scan);
      if (m.target.nodeType === 1) applyParent(m.target);
    }
  });
};

export async function startAutoTranslate() {
  const lang = getLang();
  if (lang === "en") return;
  try {
    dict = (await api.get(`/i18n/${lang}`)).data;
  } catch {
    dict = {};
  }
  const start = () => {
    scan(document.body);
    new MutationObserver(onMutations).observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ATTRS });
  };
  if (document.body) start();
  else document.addEventListener("DOMContentLoaded", start);
}
